import { useCallback } from 'react';
import { useDispatch, useSelector } from 'store';
import { closeSubDrawer } from 'store/reducers/subdrawer';
import { cn } from 'utils/cn';

// The side panel `DrawerPortal type="sub"` renders into. The destination stays mounted while the panel
// is closed, since the portal looks it up when it renders; opening slides the panel out from under the nav,
// which is on the start side, and closing slides it back under, after which it hides, while the backdrop fades
// out: each slide's `rtl:` twin runs the other way.
// `detached` draws it as the new design's box instead of flush against the nav: `width` px wide, edged by its
// shadow's ring, with rounded corners, at the top and the gap off the nav and the window's bottom
// that the new layout keeps on the document as `--panel-top` and `--panel-gap`.
function SubDrawer({ detached }: { detached?: { width: number } }) {
    const isOpen = useSelector((state) => !!state.subDrawer.activeDrawerId.length);
    const dispatch = useDispatch();

    const closeDrawer = useCallback(() => dispatch(closeSubDrawer()), [dispatch]);

    return (
        <>
            <div className="relative">
                <div
                    className={cn(
                        // `display` changes last, so the slide plays first.
                        'bg-paper border-divider absolute inset-y-0 start-0 z-(--z-sub-drawer) flex flex-col overflow-y-auto overscroll-contain border-e transition-[translate,display] transition-discrete duration-225 ease-out motion-reduce:transition-none starting:-translate-x-full rtl:starting:translate-x-full',
                        !isOpen && !detached && 'hidden -translate-x-full duration-200 rtl:translate-x-full',
                        // The box slides 70% of its width out of a fade and a blur, and leaves quicker than it came,
                        // fading into the blur while it moves 70% back; under reduced motion it fades in and out
                        // where it stands, in 150ms.
                        detached &&
                            'shadow-panel start-(--panel-gap) top-(--panel-top) bottom-(--panel-gap) rounded-2xl border-0 outline-hidden transition-[translate,opacity,filter,display] duration-(--duration-slow) ease-(--ease-smooth-out) motion-reduce:transition-[opacity,display] motion-reduce:duration-150 starting:-translate-x-[70%] starting:opacity-0 motion-safe:starting:blur-(--blur-panel) rtl:starting:translate-x-[70%]',
                        detached &&
                            !isOpen &&
                            'hidden opacity-0 duration-(--duration-fast) motion-safe:-translate-x-[70%] motion-safe:blur-(--blur-panel) motion-safe:rtl:translate-x-[70%]'
                    )}
                    style={detached && { width: detached.width }}
                >
                    <div id="sub-drawer-portal-destination" className="h-full" />
                </div>
            </div>
            {/* Stays mounted to fade out as the panel slides away, on the detached box's clocks, and with it in 150ms
                under reduced motion; closed, it lets every click through. */}
            <div
                aria-hidden
                className={cn(
                    'fixed inset-0 z-(--z-sub-drawer-backdrop) bg-black/10 transition-[opacity,display] transition-discrete duration-200 ease-out supports-backdrop-filter:backdrop-blur-xs motion-reduce:transition-none',
                    detached &&
                        'duration-(--duration-slow) ease-(--ease-smooth-out) motion-reduce:transition-[opacity,display] motion-reduce:duration-150',
                    !isOpen && 'pointer-events-none hidden opacity-0',
                    detached && !isOpen && 'duration-(--duration-fast)'
                )}
                onClick={closeDrawer}
            />
        </>
    );
}

export default SubDrawer;
